import React, { useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import { startCase } from "lodash";

import { analytics } from "@flare/analytics";
import { useTranslation } from "@flare/i18n";
import { Icon, Text } from "@flare/ui/components";

import { funnelInputsNameMap } from "@/modules/v2/funnel/config";
import { useFunnelAnswers } from "@/modules/v2/funnel/hooks/useFunnelAnswers";
import { BiEventsNames } from "@/services/analytics/bi-event-names";

import { Input } from "../base/Input";

import type { ZipCodeProps } from "./zip-code.types";
import {
  formatAddress,
  getAddress,
  isStateSupported,
  normalizeStateName,
  supportedPractices,
} from "./zip-code.utils";

export function ZipCode({
  practice = supportedPractices.FAMILY,
}: ZipCodeProps) {
  const {
    watch,
    setValue,
    setError,
    clearErrors,
    trigger,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation();
  const { field } = useController({
    name: funnelInputsNameMap.zipCode,
    rules: { required: true, minLength: 5 },
  });
  const { funnelAnswers } = useFunnelAnswers();
  const [inputText, setInputText] = useState(funnelAnswers?.zipCode || "");

  const validationErrorMessage = t("FUNNEL_ZIP_CODE_ERROR_MESSAGE");

  const county = watch("county", funnelAnswers?.county);

  const stateDisplayText = watch(
    "stateDisplayText",
    funnelAnswers?.stateDisplayText,
  );

  const formattedAddress = formatAddress({ county, state: stateDisplayText });

  const hasError = Boolean(errors?.[field.name]);
  const error =
    hasError && (errors?.[field.name]?.message || validationErrorMessage);

  const setValues = ({
    county = "",
    stateName = "",
    stateCode = "",
    stateDisplayText = "",
    zipCode = "",
  } = {}) => {
    setValue("county", county);
    setValue("state", stateName);
    setValue("stateCode", stateCode);
    setValue("stateDisplayText", stateDisplayText);
    setValue(field.name, zipCode);
  };

  async function validate(zipCode: string) {
    const { county, state, stateCode } = await getAddress(zipCode);
    const normalizedStateName = normalizeStateName(state);
    const stateName = isStateSupported(normalizedStateName, practice)
      ? normalizedStateName
      : "unsupported";
    const stateDisplayText = startCase(state);
    if (!state) {
      setError(field.name, { message: validationErrorMessage });
      setValues();
      trigger();
    } else {
      if (state && !county) {
        analytics.track(BiEventsNames.WebFunnelAlert, {
          current_step_key: field.name,
          answer_value: { zipCode, state },
          error_type: "no_county_zip_code",
        });
      }
      clearErrors();
      setValues({ county, stateName, stateCode, stateDisplayText, zipCode });
      trigger();
    }
  }

  async function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { value: newValue } = event.target;
    const isNumber = /^[\d]*$/.test(newValue);
    const isLengthValid = isNumber && newValue.length === 5;
    if (!isNumber || newValue.length > 5) return;
    setInputText(newValue);
    if (isLengthValid) {
      validate(newValue);
    } else {
      clearErrors();
      setValues();
    }
  }

  return (
    <div className="flex min-h-[114px] flex-col justify-start gap-sp-2">
      <Input
        name={field.name}
        ref={field.ref}
        inputMode="decimal"
        label={t("FUNNEL_ZIP_CODE_LABEL")}
        placeholder={t("FUNNEL_ZIP_CODE_PLACEHOLDER")}
        error={hasError}
        hint={typeof error === "string" ? error : undefined}
        value={inputText ?? ""}
        onChange={handleChange}
      />
      {formattedAddress && (
        <div className="flex items-center justify-start gap-sp-1">
          <Icon name="location" />
          <Text variant="body2">{formattedAddress}</Text>
        </div>
      )}
    </div>
  );
}
